/**
 * Single source of truth for per-env config on web.
 *
 * Resolution order (highest priority first):
 *   1. `VITE_APP_ENV` build-time override (use only for tests / preview)
 *   2. `window.location.hostname` match against the known prod / staging
 *      domains
 *   3. `import.meta.env.MODE` (dev when `pnpm dev`, production otherwise)
 *
 * Why hostname over build-time: GitLab Pages serves a single static
 * bundle for the whole project. Two custom domains
 * (budget.crawfishlabs.ai + budget-staging.crawfishlabs.ai) point at
 * the SAME bundle — without runtime detection both would route to the
 * same backend. The bundle ships both configs and picks the right one
 * on load.
 *
 * The Firebase Web SDK API keys are public client identifiers per
 * https://firebase.google.com/docs/projects/api-keys — safe to embed
 * both in the bundle.
 *
 * Mirrors iOS `EnvironmentConfig.swift` for the same dev/staging/prod
 * names + URLs.
 */

export type AppEnvironment = 'dev' | 'staging' | 'production'

export interface EnvironmentConfig {
  env: AppEnvironment
  apiBaseURL: string
  firebase: FirebaseConfig
  iam: FirebaseConfig
  sentryDsn?: string
}

interface FirebaseConfig {
  apiKey: string
  authDomain: string
  projectId: string
  appId: string
}

function read(name: string): string {
  const value = import.meta.env[name as keyof ImportMetaEnv]
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`Missing env var ${name}. See web/.env.example.`)
  }
  return value
}

function readOptional(name: string): string | undefined {
  const value = import.meta.env[name as keyof ImportMetaEnv]
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

function resolveEnv(): AppEnvironment {
  const explicit = readOptional('VITE_APP_ENV')
  if (explicit === 'dev' || explicit === 'staging' || explicit === 'production') {
    return explicit
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname
    if (host === 'budget.crawfishlabs.ai') return 'production'
    if (host === 'budget-staging.crawfishlabs.ai') return 'staging'
  }
  return import.meta.env.MODE === 'development' ? 'dev' : 'production'
}

const env = resolveEnv()

const API_BY_ENV: Record<AppEnvironment, string> = {
  dev: 'https://us-central1-crawfish-budget-dev.cloudfunctions.net/api/v1',
  staging: 'https://us-central1-crawfish-budget-staging.cloudfunctions.net/api/v1',
  production: 'https://us-central1-crawfish-budget-production.cloudfunctions.net/api/v1',
}

const FIREBASE_AUTH_DOMAIN: Record<AppEnvironment, string> = {
  dev: 'crawfish-budget-dev.firebaseapp.com',
  staging: 'crawfish-budget-staging.firebaseapp.com',
  production: 'crawfish-budget-production.firebaseapp.com',
}

const FIREBASE_PROJECT_ID: Record<AppEnvironment, string> = {
  dev: 'crawfish-budget-dev',
  staging: 'crawfish-budget-staging',
  production: 'crawfish-budget-production',
}

const IAM_AUTH_DOMAIN: Record<AppEnvironment, string> = {
  dev: 'crawfish-iam-dev.firebaseapp.com',
  staging: 'crawfish-iam-staging.firebaseapp.com',
  production: 'crawfish-iam-prod.firebaseapp.com',
}

const IAM_PROJECT_ID: Record<AppEnvironment, string> = {
  dev: 'crawfish-iam-dev',
  staging: 'crawfish-iam-staging',
  production: 'crawfish-iam-prod',
}

// Per-env API keys + app IDs come from env vars so prod values don't
// leak into PR previews of feature branches. CI provides all three
// sets for the published bundle; local dev only needs the DEV pair.
//
// `read()` throws when a required var is missing, but only for the
// CURRENT env — so a dev who hasn't filled the staging+prod blanks
// can still run `pnpm dev` against crawfish-budget-dev.
function firebaseConfigFor(target: AppEnvironment): FirebaseConfig {
  const prefix = `VITE_FIREBASE_${target.toUpperCase()}` as const
  return {
    apiKey: read(`${prefix}_API_KEY`),
    authDomain: FIREBASE_AUTH_DOMAIN[target],
    projectId: FIREBASE_PROJECT_ID[target],
    appId: read(`${prefix}_APP_ID`),
  }
}

function iamConfigFor(target: AppEnvironment): FirebaseConfig {
  const prefix = `VITE_IAM_${target.toUpperCase()}` as const
  return {
    apiKey: read(`${prefix}_API_KEY`),
    authDomain: IAM_AUTH_DOMAIN[target],
    projectId: IAM_PROJECT_ID[target],
    appId: read(`${prefix}_APP_ID`),
  }
}

export const environment: EnvironmentConfig = {
  env,
  apiBaseURL: API_BY_ENV[env],
  firebase: firebaseConfigFor(env),
  iam: iamConfigFor(env),
  sentryDsn: readOptional('VITE_SENTRY_DSN'),
}
